"use client";

import React, { useMemo, useState, useEffect } from "react";
import { Flame, RotateCcw, PackageX, Loader as Loader2, ArrowRight, Search, X, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { decodeEntities } from "@/lib/decodeEntities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice, ProductSortDropdown } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface ProductListingsProps {
  initialTerm?: {
    id?: string | number;
    slug?: string;
    taxonomy?: string;
    name?: string;
  };
}
function CategoryPills({
  selectedCategory,
  onSelectCategory
}: {
  selectedCategory: string;
  onSelectCategory: (slug: string) => void;
}) {
  const {
    terms,
    loading
  } = useEcommerceTerms();
  const visibleCategories = useMemo(() => {
    return (terms || []).filter(cat => cat && cat.slug !== "warranty-plans" && cat.slug !== "uncategorized" && typeof cat.count === "number" && cat.count > 0);
  }, [terms]);
  if (loading && visibleCategories.length === 0) {
    return <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        <div className="h-8 w-20 rounded-md bg-muted animate-pulse" />
        <div className="h-8 w-28 rounded-md bg-muted animate-pulse" />
        <div className="h-8 w-32 rounded-md bg-muted animate-pulse" />
      </div>;
  }
  return <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none flex-wrap sm:flex-nowrap">
      <Button variant={selectedCategory === "" ? "default" : "outline"} size="sm" onClick={() => onSelectCategory("")} className="rounded-md h-8 text-xs font-medium shrink-0">
        All Models
      </Button>
      {visibleCategories.map((cat, i) => <Button key={cat.slug || cat.id} data-index={i} variant={selectedCategory === cat.slug ? "default" : "outline"} size="sm" onClick={() => onSelectCategory(cat.slug || "")} className="rounded-md h-8 text-xs font-medium whitespace-nowrap shrink-0">
          {decodeEntities(cat.name)}
          {typeof cat.count === "number" && <span className="ml-1.5 opacity-60 text-[10px]">({cat.count})</span>}
        </Button>)}
    </div>;
}
function ProductGridConsumer({
  selectedCategory,
  activeSearch,
  onSelectCategory,
  onSearchChange,
  onResetAll,
  isArchivePage
}: {
  selectedCategory: string;
  activeSearch: string;
  onSelectCategory: (slug: string) => void;
  onSearchChange: (val: string) => void;
  onResetAll: () => void;
  isArchivePage: boolean;
}) {
  const {
    posts: products,
    loading,
    error,
    total,
    totalPages,
    currentPage,
    hasNext,
    hasPrev,
    setPage,
    refetch
  } = useEcommerceProducts();
  const [searchInput, setSearchInput] = useState(activeSearch);
  useEffect(() => {
    setSearchInput(activeSearch);
  }, [activeSearch]);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchChange(searchInput.trim());
  };
  const handleSearchClear = () => {
    setSearchInput("");
    onSearchChange("");
  };
  const isFiltered = Boolean(selectedCategory || activeSearch);
  return <div className="space-y-6">
      {/* Toolbar: Category Pills (if not dedicated archive), Search & Sort */}
      <div className="p-4 bg-card rounded-md border border-border shadow-xs flex flex-col gap-4">
        {/* Top row: Categories (hidden on archive pages when initialTerm is already fixed) */}
        {!isArchivePage && <div className="w-full border-b border-border pb-3">
            <CategoryPills selectedCategory={selectedCategory} onSelectCategory={onSelectCategory} />
          </div>}

        {/* Controls row: Search input, Sort dropdown, and Clear filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Search bar */}
          <form onSubmit={handleSearchSubmit} className="relative flex-1 max-w-md flex items-center">
            <Search className="absolute left-3 size-4 text-muted-foreground pointer-events-none" />
            <Input type="search" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search water heaters by name or spec..." className="pl-9 pr-9 h-9 text-xs rounded-md w-full bg-background" />
            {searchInput && <button type="button" onClick={handleSearchClear} className="absolute right-2.5 text-muted-foreground hover:text-foreground" aria-label="Clear search">
                <X className="size-3.5" />
              </button>}
          </form>

          {/* Right side: Result count, Sort, and Reset */}
          <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
            {/* Live Result Count */}
            <div className="text-xs text-muted-foreground font-mono">
              {!loading && <span>
                  {total} {total === 1 ? "unit" : "units"}
                </span>}
            </div>

            <ProductSortDropdown label="" placeholder="Sort by" />

            {isFiltered && <Button variant="ghost" size="sm" onClick={onResetAll} className="h-9 text-xs text-muted-foreground hover:text-foreground px-2" title="Reset All Filters">
                <RotateCcw className="size-3.5 mr-1" />
                Reset
              </Button>}
          </div>
        </div>
      </div>

      {/* Content State: Loading, Error, Empty, or Product Grid */}
      {loading ? <div className="py-12">
          <div className="flex items-center justify-center gap-2 mb-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin text-primary" />
            <span>Loading natural gas models...</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, idx) => <div key={idx} data-index={idx} className="bg-card rounded-md border border-border p-4 flex flex-col justify-between gap-4">
                <Skeleton className="w-full h-56 rounded-md" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-5 w-4/5" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <Skeleton className="h-9 w-full rounded-md" />
              </div>)}
          </div>
        </div> : error ? <div className="p-12 text-center rounded-md border border-destructive/20 bg-destructive/5 max-w-lg mx-auto my-12">
          <PackageX className="size-10 text-destructive mx-auto mb-3" />
          <h3 className="text-lg font-bold font-serif text-foreground mb-1">
            Unable to load catalog
          </h3>
          <p className="text-sm text-muted-foreground mb-6">{error}</p>
          <Button variant="outline" onClick={() => refetch()}>
            Retry Loading Catalog
          </Button>
        </div> : products.length === 0 ? (/* Empty State */
    <div className="p-12 md:p-16 text-center rounded-md border border-dashed border-border bg-card max-w-xl mx-auto my-12">
          <div className="w-12 h-12 rounded-md bg-muted border border-border flex items-center justify-center mx-auto mb-4 text-muted-foreground">
            <SlidersHorizontal className="size-5" />
          </div>
          <h3 className="text-xl font-bold font-serif text-foreground mb-2">
            No water heaters found
          </h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
            {activeSearch ? `No products matched "${activeSearch}". Try checking your spelling or broadening your terms.` : "No units match your selected criteria. Try resetting filters to view all models."}
          </p>
          <Button onClick={onResetAll} variant="default" className="rounded-md">
            Reset All Filters
          </Button>
        </div>) : (/* Products Grid */
    <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product, index) => {
          const imgUrl = product.featured_image_url || product.gallery_image_urls && product.gallery_image_urls[0] || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1812570/img/water_heater_unit_front.webp";
          const isAvailable = product.is_in_stock !== false;
          const productLink = product.link || `/shop`;
          const isVariable = product.type === "variable";
          const decodedTitle = decodeEntities(product.title || "Natural Gas Water Heater");
          return <div key={product.id} data-index={index} className="group bg-card rounded-md border border-border flex flex-col justify-between hover:shadow-md transition-all duration-200 overflow-hidden">
                  {/* Image Container with Badges */}
                  <div className="relative aspect-square bg-muted/40 p-4 border-b border-border flex items-center justify-center overflow-hidden">
                    <Link to={productLink} className="w-full h-full flex items-center justify-center" tabIndex={-1}>
                      <img src={imgUrl} alt={decodedTitle} loading="lazy" className="max-h-full max-w-full object-contain group-hover:scale-102 transition-transform duration-300" />
                    </Link>

                    {/* Status Badges */}
                    <div className="absolute top-2.5 left-2.5 flex flex-col gap-1 z-10 pointer-events-none">
                      {product.on_sale && <span className="px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground text-[10px] font-bold uppercase tracking-wider">
                          Sale
                        </span>}
                      {isVariable && <span className="px-2 py-0.5 rounded-md bg-primary text-primary-foreground text-[10px] font-semibold uppercase tracking-wider">
                          Options
                        </span>}
                    </div>

                    <div className="absolute bottom-2.5 right-2.5 z-10 pointer-events-none">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold border ${isAvailable ? "bg-background/90 text-foreground border-border" : "bg-destructive/10 text-destructive border-destructive/20"}`}>
                        {isAvailable ? "In Stock" : "Backorder"}
                      </span>
                    </div>
                  </div>

                  {/* Product Details */}
                  <div className="p-4 flex flex-col flex-grow justify-between gap-4">
                    <div>
                      {/* Primary Category Label */}
                      <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1 line-clamp-1">
                        {decodeEntities(product.categories?.[0]?.name || "Natural Gas Heating")}
                      </div>

                      {/* Product Title */}
                      <h3 className="font-serif font-bold text-base text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                        <Link to={productLink}>{decodedTitle}</Link>
                      </h3>

                      {/* Brief Specs line if available */}
                      {product.short_description && <div className="mt-1 text-xs text-muted-foreground line-clamp-2 font-sans prose prose-sm max-w-none [&_p]:m-0" dangerouslySetInnerHTML={{
                  __html: decodeEntities(product.short_description)
                }} />}
                    </div>

                    {/* Pricing and Action Block */}
                    <div className="pt-3 border-t border-border flex flex-col gap-3">
                      <div className="flex items-baseline justify-between">
                        <div className="flex flex-col">
                          <ProductPrice product={product} className="text-lg font-bold font-serif text-foreground" />
                        </div>

                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Flame className="size-3.5 text-primary" />
                          <span>Natural Gas</span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="grid grid-cols-2 gap-2 items-center">
                        <Button asChild variant="outline" size="sm" className="w-full text-xs font-semibold rounded-md h-9">
                          <Link to={productLink}>
                            Specs
                            <ArrowRight className="size-3.5 ml-1" />
                          </Link>
                        </Button>

                        <div className="w-full">
                          <AddToCartButton product={product} productId={product.id} showQuantity={false} variant="default" size="sm" buttonClassName="w-full text-xs font-semibold rounded-md h-9 bg-primary text-primary-foreground hover:bg-primary/90" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && <div className="mt-12 pt-8 border-t border-border flex items-center justify-between gap-4">
              <div className="text-xs text-muted-foreground font-mono">
                Page {currentPage} of {totalPages} ({total} total units)
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={!hasPrev} onClick={() => setPage(Math.max(1, currentPage - 1))} className="h-8 text-xs rounded-md">
                  Previous
                </Button>

                <div className="hidden sm:flex items-center gap-1">
                  {[...Array(totalPages)].map((_, i) => {
              const pNum = i + 1;
              return <Button key={pNum} variant={currentPage === pNum ? "default" : "outline"} size="sm" onClick={() => setPage(pNum)} className="h-8 w-8 text-xs p-0 rounded-md">
                        {pNum}
                      </Button>;
            })}
                </div>

                <Button variant="outline" size="sm" disabled={!hasNext} onClick={() => setPage(Math.min(totalPages, currentPage + 1))} className="h-8 text-xs rounded-md">
                  Next
                </Button>
              </div>
            </div>}
        </>)}
    </div>;
}
export default function ProductListings({
  initialTerm
}: ProductListingsProps = {}) {
  const [selectedCategory, setSelectedCategory] = React.useState<string>(() => {
    if (initialTerm?.slug) return initialTerm.slug;
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("category") || "";
    }
    return "";
  });
  const [activeSearch, setActiveSearch] = React.useState<string>(() => {
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      return sp.get("s") || sp.get("search") || "";
    }
    return "";
  });

  // Keep in sync with browser URL changes
  React.useEffect(() => {
    const syncFromUrl = () => {
      const sp = new URLSearchParams(window.location.search);
      const cat = sp.get("category") || initialTerm?.slug || "";
      const s = sp.get("s") || sp.get("search") || "";
      setSelectedCategory(cat);
      setActiveSearch(s);
    };
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [initialTerm?.slug]);
  const handleSelectCategory = (slug: string) => {
    setSelectedCategory(slug);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (slug) {
        url.searchParams.set("category", slug);
      } else {
        url.searchParams.delete("category");
      }
      window.history.pushState({}, "", url.pathname + url.search);
      window.dispatchEvent(new Event("popstate"));
    }
  };
  const handleSearchChange = (val: string) => {
    setActiveSearch(val);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      if (val) {
        url.searchParams.set("s", val);
      } else {
        url.searchParams.delete("s");
        url.searchParams.delete("search");
      }
      window.history.pushState({}, "", url.pathname + url.search);
      window.dispatchEvent(new Event("popstate"));
    }
  };
  const handleResetAll = () => {
    setSelectedCategory("");
    setActiveSearch("");
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("category");
      url.searchParams.delete("s");
      url.searchParams.delete("search");
      window.history.pushState({}, "", url.pathname + url.search);
      window.dispatchEvent(new Event("popstate"));
    }
  };

  // Construct WP_Query based on current URL parameters, search, and preselected category
  const wpQuery = useMemo(() => {
    const client = getWvcClient();
    const wpParams = client && typeof client.getCurrentWPQueryParams === "function" ? client.getCurrentWPQueryParams() : {};
    const vars: Record<string, any> = {
      ...wpParams,
      post_type: "product",
      per_page: 12
    };
    if (activeSearch) {
      vars.s = activeSearch;
    }
    if (selectedCategory) {
      vars.product_cat = selectedCategory;
    } else if (initialTerm?.slug && initialTerm.taxonomy === "product_cat") {
      vars.product_cat = initialTerm.slug;
    } else if (initialTerm?.slug && initialTerm.taxonomy === "product_tag") {
      vars.product_tag = initialTerm.slug;
    }
    return new WP_Query(vars);
  }, [selectedCategory, activeSearch, initialTerm]);
  const termsQuery = useMemo(() => {
    return new WP_Term_Query({
      taxonomy: "product_cat",
      per_page: 30,
      hide_empty: true
    });
  }, []);
  return <section id="product-listings" data-nav="light" className="relative bg-background text-foreground py-12 md:py-20 border-b border-border">
      <div className="max-w-[1240px] mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-border mb-6">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-secondary/10 border border-secondary/20 text-secondary text-xs font-bold uppercase tracking-wider font-mono mb-2">
              <Flame className="size-3.5" />
              <span>Certified Natural Gas Hardware</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight font-serif text-foreground">
              {initialTerm?.name ? `${decodeEntities(initialTerm.name)} Equipment` : "Water Heating Equipment"}
            </h2>
            <p className="mt-1.5 text-muted-foreground text-xs md:text-sm max-w-2xl font-sans">
              Precision-engineered residential tank, tankless, and specialty natural gas models configured for reliable recovery and everyday domestic service.
            </p>
          </div>
        </div>

        {/* Categories terms provider wrapping the products provider */}
        <EcommerceTermsProvider wp_term_query={termsQuery}>
          <EcommerceProductsProvider wp_query={wpQuery}>
            <ProductGridConsumer selectedCategory={selectedCategory} activeSearch={activeSearch} onSelectCategory={handleSelectCategory} onSearchChange={handleSearchChange} onResetAll={handleResetAll} isArchivePage={Boolean(initialTerm?.slug)} />
          </EcommerceProductsProvider>
        </EcommerceTermsProvider>
      </div>
    </section>;
}